import { evaluateDrinkingWindow, windowYearsAreCoherent, } from "./drinking-window.js";
import { drinkingUrgency, URGENCY_ORDER, yearsOfGrace } from "./drinking-urgency.js";
/**
 * Offered in this order: the one the list opens in first, then the four of
 * `10.19` in the order they were asked for, with the peak beside the window it
 * is a part of.
 */
export const WINE_ORDER_KEYS = [
    "added",
    "number",
    "window",
    "peak",
    "vintage",
    "producer",
];
/**
 * The orders a list can be read in, given whether its rows say when each wine
 * was added.
 *
 * Only the owner's rows do — the decision of 2026-09-26 in `project-scope.md`
 * — and a key that sorts every row into a tie is a control that does nothing,
 * so a list without the moment is not offered it.
 */
export function wineOrderKeys(dated) {
    return dated ? WINE_ORDER_KEYS : WINE_ORDER_KEYS.filter((key) => key !== "added");
}
/**
 * The order the list opens in.
 *
 * The owner's opens on what was added last, which is what they asked for on
 * 2026-09-26. Everybody else's is the list as it has always been, by the number
 * on the label, lowest first — the order the bottles stand in on the shelves.
 * That is also where the owner's list lands for the one release in which the
 * browser can arrive before an API that sends the moment.
 */
export function defaultWineOrder(dated) {
    return { key: dated ? "added" : "number", direction: "asc" };
}
/** Whether the list is being read in anything but its own order — what turns
 *  the control brass. */
export function wineOrderIsActive(order, dated) {
    const resting = defaultWineOrder(dated);
    return order.key !== resting.key || order.direction !== resting.direction;
}
/**
 * Where a wine falls in *what is in a hurry*, as a tuple compared left to
 * right, or null for a wine that has no window to be in a hurry about.
 *
 * **The first two places are `Att öppna`'s own order**, borrowed rather than
 * restated: the group in `URGENCY_ORDER`, then the years of grace within it. A
 * list sorted by window and the page of what to open would otherwise be two
 * answers to one question, and they would disagree about which bottle comes
 * first — the drift `drinking-urgency.ts` was put in `shared/` to prevent.
 *
 * The page stops there, because it lists only the wines in a hurry. The list
 * holds every wine, so the rest need a place too: after the five groups, by the
 * year the window closes and then the year it opens. That puts a wine closing
 * in 2034 before one closing in 2046, and among wines with no closing year
 * written, the one that is drinkable soonest first — which is the same question
 * asked one step further out.
 *
 * Null for no years at all and for years that contradict each other. The first
 * is what *Luckor* is for; the second has no honest place in a sequence, and
 * guessing which of its years to believe is exactly what the coherence check
 * refuses to do everywhere else.
 */
function windowKey(wine, currentYear) {
    const years = [wine.drinkFrom, wine.peakFrom, wine.peakTo, wine.drinkBy];
    if (years.every((year) => year === null))
        return null;
    if (!windowYearsAreCoherent(wine))
        return null;
    const urgency = drinkingUrgency(wine, currentYear);
    if (urgency !== null) {
        return [URGENCY_ORDER.indexOf(urgency), yearsOfGrace(wine, urgency, currentYear), 0];
    }
    return [
        URGENCY_ORDER.length,
        wine.drinkBy ?? Number.MAX_SAFE_INTEGER,
        wine.drinkFrom ?? Number.MAX_SAFE_INTEGER,
    ];
}
/**
 * Where a wine stands against its peak, as a tuple compared left to right, or
 * null for a wine with nothing that says when a peak ends.
 *
 * **Three places, in the order they were asked for**: the peak already behind
 * it, then the peak it is in now, then the peak still to come — and within the
 * last, the one with the shortest wait first. The middle group was not named
 * when it was asked for, and it stands where *time until the peak* puts it: a
 * wine at its peak has no wait left, which is less than any wine still waiting
 * and more than one whose peak is over.
 *
 * Within the groups the number is years, so it reads the way the window key's
 * does. A peak that ended longest ago comes first, because it is the one that
 * has been waiting longest to be drunk. A peak that is on now is ordered by the
 * year it ends, nearest first, and one with no end written sorts last among
 * them, since nothing about it is running out — the rule `yearsOfGrace` gives
 * the same group on `Att öppna`.
 *
 * Which group a wine is in is asked of `evaluateDrinkingWindow` rather than
 * re-derived from `peakFrom` and `peakTo`, so what this order calls *at its
 * peak* is what the band draws and the filter narrows by. That also settles the
 * half-written peaks: a lone `peakTo` is a peak since the wine was drinkable,
 * and a lone `peakFrom` one that has not been given an end.
 *
 * **With no peak written, `drinkBy` is the end of the peak.** Asked for by the
 * owner on 2026-10-07, the same day as the order: a wine that says only when
 * it must be drunk is read as being at its best until then. It is the state
 * most of this cellar is in, so without it the order would put four wines in
 * five at the bottom. A `drinkFrom` beside it is kept, and becomes the peak's
 * start by the half-written rule above — so a wine not yet drinkable waits for
 * it among the peaks still to come.
 *
 * Only for the order. Nothing is written back, and the band, the filter and
 * `Att öppna` still read a lone `drinkBy` as *ready*: that the year is the end
 * of a peak is a way of sorting, not a judgement the cellar has made.
 *
 * Null for a wine with neither peak years nor `drinkBy` — a lone `drinkFrom`
 * says nothing about when anything ends — and, as everywhere, for years that
 * contradict each other.
 */
function peakKey(wine, currentYear) {
    if (!windowYearsAreCoherent(wine))
        return null;
    const unwritten = wine.peakFrom === null && wine.peakTo === null;
    if (unwritten && wine.drinkBy === null)
        return null;
    const years = unwritten ? { ...wine, peakTo: wine.drinkBy } : wine;
    if (years.peakTo !== null && years.peakTo < currentYear) {
        return [0, years.peakTo];
    }
    const state = evaluateDrinkingWindow(years, currentYear);
    // A peak with no end written is over when the window is: the plateau cannot
    // outlast the last year the wine is worth drinking, and that year is then
    // the nearest thing to its end the cellar has.
    if (state === "past") {
        return [0, years.drinkBy ?? currentYear];
    }
    if (state === "peak") {
        return [1, years.peakTo ?? Number.MAX_SAFE_INTEGER];
    }
    // Not past it and not in it: the peak is ahead. It begins at `peakFrom`, or —
    // for a lone `peakTo` on a wine not yet drinkable — at `drinkFrom`, since a
    // peak with no start written is one from the first drinkable year. One of the
    // two is always there, or the evaluation would have said `peak`.
    return [2, years.peakFrom ?? years.drinkFrom ?? Number.MAX_SAFE_INTEGER];
}
/**
 * The year a wine is sorted by, or null for one that has none.
 *
 * A wine without a vintage has none even when it carries a base vintage. The
 * base year is a fact about the blend, printed small on the back label if at
 * all; sorting a Champagne among the 2019s because most of it was 2019 would
 * place it where nobody looking for 2019 expects it.
 */
function vintageKey(wine) {
    return wine.isNonVintage ? null : wine.vintage;
}
/**
 * Swedish collation, the same one the country list and the vocabulary pages
 * use, so `Å` and `Ö` fall after `Z` where a Swedish reader looks for them.
 *
 * One consequence is accepted rather than worked around: Swedish collation reads
 * `ü` as `y`, so `Müller` sorts among the `My`. Folding accents first would fix
 * that and move every `Å` in among the `A` instead — and no producer in this
 * cellar begins with `Ü`, while the choice of collation is one every name is
 * sorted by. Names are compared as written, so the `Château`s stand together.
 */
const collator = new Intl.Collator("sv");
/** Numbers where smaller comes first, compared place by place. */
function compareTuples(a, b) {
    for (let at = 0; at < a.length; at += 1) {
        const difference = (a[at] ?? 0) - (b[at] ?? 0);
        if (difference !== 0)
            return difference;
    }
    return 0;
}
/**
 * The wines in the order asked for, as a new list.
 *
 * **A wine with nothing to sort by goes last in both directions.** Turning the
 * order round means *the other end of the years first*, not *the wines without
 * a year first*: a non-vintage Champagne is not older than 1998, and a wine
 * nobody has given a window is not the least urgent one in the cellar. Putting
 * them at the top of a descending list would make the direction button a way
 * of hiding every dated wine below a block of undated ones.
 *
 * **Ties fall back to the wine number, always lowest first**, whichever way the
 * key runs — two 2019s come in the order they stand on the shelf, which is the
 * rule `groupByUrgency` breaks its ties by too. The producer order ties on the
 * wine's name and then its vintage before the number, because five bottles of
 * one house read best as that house's list.
 */
export function orderWines(wines, order, currentYear) {
    const sign = order.direction === "asc" ? 1 : -1;
    const keyed = wines.map((wine) => ({
        wine,
        // The window and the peak are both a tuple, and compared the same way once
        // they are one; only the key the order reads is worked out.
        window: order.key === "window"
            ? windowKey(wine, currentYear)
            : order.key === "peak"
                ? peakKey(wine, currentYear)
                : null,
        vintage: vintageKey(wine),
    }));
    return keyed
        .sort((a, b) => {
        switch (order.key) {
            case "added": {
                // Newest first is the natural end, so `asc` compares the other way
                // round from every other key. The moments are ISO instants from one
                // server, so they compare as text in the order they happened.
                const x = a.wine.addedAt ?? null;
                const y = b.wine.addedAt ?? null;
                if (x === null || y === null) {
                    return (Number(x === null) - Number(y === null) || a.wine.number - b.wine.number);
                }
                return sign * (x < y ? 1 : x > y ? -1 : 0) || a.wine.number - b.wine.number;
            }
            case "number":
                return sign * (a.wine.number - b.wine.number);
            case "window":
            case "peak": {
                if (a.window === null || b.window === null) {
                    return (Number(a.window === null) - Number(b.window === null) ||
                        a.wine.number - b.wine.number);
                }
                return (sign * compareTuples(a.window, b.window) || a.wine.number - b.wine.number);
            }
            case "vintage": {
                if (a.vintage === null || b.vintage === null) {
                    return (Number(a.vintage === null) - Number(b.vintage === null) ||
                        a.wine.number - b.wine.number);
                }
                return sign * (a.vintage - b.vintage) || a.wine.number - b.wine.number;
            }
            case "producer":
                return (sign *
                    (collator.compare(a.wine.producer, b.wine.producer) ||
                        collator.compare(a.wine.name, b.wine.name)) ||
                    (a.vintage ?? Number.MAX_SAFE_INTEGER) -
                        (b.vintage ?? Number.MAX_SAFE_INTEGER) ||
                    a.wine.number - b.wine.number);
        }
    })
        .map(({ wine }) => wine);
}
//# sourceMappingURL=wine-order.js.map